import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLocale } from '@/contexts/LocaleContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { getImports, updateImportStatus } from '@/lib/api';
import { 
  Ship, 
  Clock,
  Building2,
  Plus,
  MapPin
} from 'lucide-react';
import { toast } from 'sonner';

const getStatusLabels = (isUSA) => ({
  nueva: isUSA ? 'New' : 'Nueva',
  en_revision: isUSA ? 'In review' : 'En revisión',
  cotizada: isUSA ? 'Quoted' : 'Cotizada',
  negociacion: isUSA ? 'Negotiation' : 'Negociación',
  cerrada: isUSA ? 'Closed' : 'Cerrada',
  cancelada: isUSA ? 'Cancelled' : 'Cancelada'
});

const ImportList = () => {
  const navigate = useNavigate();
  const { isUSA } = useLocale();
  const statusLabels = getStatusLabels(isUSA);
  const [allImports, setAllImports] = useState([]);
  const [sentImports, setSentImports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchImports();
  }, []);

  const fetchImports = async () => {
    try {
      const [all, sent] = await Promise.all([
        getImports('all'),
        getImports('sent')
      ]);
      setAllImports(all);
      setSentImports(sent);
    } catch (error) {
      console.error('Error fetching imports:', error);
    } finally {
      setLoading(false);
    }
  };

  const ImportCard = ({ importItem, showRequester = false }) => (
    <Card className="card-industrial hover-lift" data-testid={`import-card-${importItem.import_id}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="font-semibold text-slate-900">{importItem.fabric_type}</h3>
            {showRequester && (
              <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
                <Building2 className="w-4 h-4" />
                {importItem.requester_company_name}
              </div>
            )}
            <div className="mt-2 space-y-1 text-sm text-slate-600">
              {importItem.composition && <p>{isUSA ? 'Composition: ' : 'Composición: '}{importItem.composition}</p>}
              {importItem.weight_gsm && <p>{isUSA ? 'Weight: ' : 'Gramaje: '}{importItem.weight_gsm} g/m²</p>}
              {importItem.estimated_volume && <p>{isUSA ? 'Volume: ' : 'Volumen: '}{importItem.estimated_volume.toLocaleString(isUSA ? 'en-US' : 'es-MX')} {isUSA ? 'units' : 'unidades'}</p>}
            </div>
          </div>
          <Badge className={`status-${importItem.status}`}>
            {statusLabels[importItem.status] || importItem.status}
          </Badge>
        </div>

        <div className="mt-4 flex items-center gap-2 text-sm text-slate-500">
          <MapPin className="w-4 h-4" />
          {isUSA ? 'Delivery in: ' : 'Entrega en: '}{importItem.delivery_city}
        </div>

        {importItem.desired_delivery_date && (
          <p className="text-sm text-slate-500 mt-1">
            {isUSA ? 'Desired date: ' : 'Fecha deseada: '}{new Date(importItem.desired_delivery_date).toLocaleDateString(isUSA ? 'en-US' : 'es-MX')}
          </p>
        )}

        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-1 text-xs text-slate-500">
            <Clock className="w-3 h-3" />
            {new Date(importItem.created_at).toLocaleDateString(isUSA ? 'en-US' : 'es-MX')}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="imports-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="page-title">{isUSA ? 'Import Scheduling' : 'Programación de Importación'}</h1>
          <p className="page-subtitle">{isUSA ? 'Schedule imported fabrics with specialized suppliers' : 'Programa telas de importación con proveedores especializados'}</p>
        </div>
        <Link to="/dashboard/imports/new">
          <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-import-btn">
            <Plus className="mr-2 w-4 h-4" />
            {isUSA ? 'New schedule' : 'Nueva programación'}
          </Button>
        </Link>
      </div>

      <Tabs defaultValue="all">
        <TabsList>
          <TabsTrigger value="all" data-testid="all-imports-tab">
            {isUSA ? 'All' : 'Todas'} ({allImports.length})
          </TabsTrigger>
          <TabsTrigger value="sent" data-testid="my-imports-tab">
            {isUSA ? 'My requests' : 'Mis solicitudes'} ({sentImports.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-6">
          {allImports.length === 0 ? (
            <div className="empty-state">
              <Ship className="empty-state-icon" />
              <h3 className="empty-state-title">{isUSA ? 'No import schedules' : 'No hay programaciones de importación'}</h3>
              <p className="empty-state-text">
                {isUSA ? 'Schedule requests will appear here' : 'Las solicitudes de programación aparecerán aquí'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {allImports.map((importItem) => (
                <ImportCard key={importItem.import_id} importItem={importItem} showRequester />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="sent" className="mt-6">
          {sentImports.length === 0 ? (
            <div className="empty-state">
              <Ship className="empty-state-icon" />
              <h3 className="empty-state-title">{isUSA ? "You haven't created schedules" : 'No has creado programaciones'}</h3>
              <p className="empty-state-text">
                {isUSA ? 'Create a new import fabric schedule' : 'Crea una nueva programación de tela de importación'}
              </p>
              <Link to="/dashboard/imports/new">
                <Button className="mt-4 bg-emerald-600 hover:bg-emerald-700">
                  <Plus className="mr-2 w-4 h-4" />
                  {isUSA ? 'New schedule' : 'Nueva programación'}
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {sentImports.map((importItem) => (
                <ImportCard key={importItem.import_id} importItem={importItem} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ImportList;
